import { useEffect, useRef } from "react";
import { buckets, usesWerke, packageStates, useTransportV2 } from "../context/TransportV2Context";
import { usePrecheckStream } from "../hooks/usePrecheckStream";
import { validPlantNaming } from "../utils/plantNaming";

const PARALLEL_PACKAGES = 3;
let sequence = 0;

function PackageComparison({ draft, generation }) {
  const { dispatch } = useTransportV2();
  const stream = usePrecheckStream();
  const job = useRef(null);
  if (!job.current) job.current = { draft, generation, dispatch, run: stream.run, token: ++sequence };
  useEffect(() => {
    const { draft: initial, generation, dispatch, run, token } = job.current;
    const scope = { pkgId: initial.pkgId, generation, token };
    const controller = new AbortController();
    let alive = true;
    dispatch({ type: "PACKAGE_PRECHECK_START", ...scope });
    run({ sourceInstanceId: initial.sourceInstanceId, sourcePackageId: initial.pkgId,
      targetInstances: [initial.space.qasInstance],
      werke: usesWerke(initial) ? buckets(initial) : [],
      plantNaming: initial.plantNaming,
      // Three packages share the request budget; plant buckets run one at a time.
      concurrency: 1, signal: controller.signal,
    }).then(result => {
      if (alive) dispatch({ type: "PACKAGE_PRECHECK_DONE", ...scope, result });
    }).catch(error => {
      if (alive) dispatch({ type: "PACKAGE_PRECHECK_ERROR", ...scope, error: error?.message });
    });
    return () => { alive = false; controller.abort(); };
  }, []);
  useEffect(() => {
    const { draft, generation, dispatch, token } = job.current;
    dispatch({ type: "PACKAGE_PRECHECK_PROGRESS", pkgId: draft.pkgId, generation, token, phases: stream.phases });
  }, [stream.phases]);
  return null;
}

// Mounted above the scene router so switching packages or opening configuration does not
// stop another package's comparison. Finished and failed packages are not fetched again.
export default function PackagePrechecks({ preview = false }) {
  const { state } = useTransportV2();
  if (!state.selectedPackages.length || state.batch || state.batchRequest || !state.space?.qasInstance
    || !validPlantNaming(state.plantNaming)) return null;
  if (preview ? state.step !== 2 : state.selectedPackages.length < 2 || state.step < 3 || state.plantNaming?.mode === "existing") return null;
  const pending = packageStates(state).filter(d => ["idle", "queued", "running"].includes(d.precheckState));
  pending.sort((a, b) => Number(b.precheckState === "running") - Number(a.precheckState === "running"));
  return pending.slice(0, PARALLEL_PACKAGES).map(draft => <PackageComparison
    key={`${state.precheckGeneration}:${draft.pkgId}:${draft.precheckRequest}`}
    draft={draft} generation={state.precheckGeneration} />);
}
